/**
 * Renderizador puro da capa: JSON do template → strings de HTML.
 *
 * Este arquivo é auto-contido DE PROPÓSITO — sem imports de Angular, DOM,
 * i18n ou entidades do app. Ele é a fonte única do layout da capa e deve
 * rodar também fora do browser (Node, na API) para gerar o HTML que o
 * PdfEngine manda pro Chromium. Não adicione imports aqui.
 */

// * [Tipos estruturais — espelham os partials persistidos no Mongo]

export interface CoverRendererStylesPlatform {
    marginTop?: number;
    marginBottom?: number;
    marginLeft?: number;
    marginRight?: number;
    width?: number;
    height?: number;
    borderSize?: number;
    borderColor?: string;
    fontStyle?: string;
    lineHeight?: string;
    fontWeight?: string;
    fontSize?: string;
}

export interface CoverRendererConfigStyles {
    position?: string; // LEFT | CENTER | RIGHT
    color?: string;
    webConfigs?: CoverRendererStylesPlatform;
    pdfConfigs?: CoverRendererStylesPlatform;
    appConfigs?: CoverRendererStylesPlatform;
}

export interface CoverRendererItemValue {
    // TEXT
    text?: string;
    // IMAGE
    url?: string;
    format?: string;
    // DICTIONARY
    key?: string;
    value?: string;
    tagKeyDescription?: string;
    // comum
    linkValueConverted?: string;
}

export interface CoverRendererItem {
    type?: string; // TEXT | IMAGE | DICTIONARY | DIVIDER
    isVisible?: boolean;
    toTranslate?: boolean;
    value?: CoverRendererItemValue;
    configStyles?: CoverRendererConfigStyles;
}

export interface CoverRendererBackgroundColor {
    id?: string;
    color?: string;
}

export interface CoverRendererBackground {
    value?: string;
    colors?: CoverRendererBackgroundColor[];
    boxFit?: string;
    imageAlignment?: string;
}

export interface CoverRendererTemplate {
    background?: CoverRendererBackground;
    items?: CoverRendererItem[];
}

export interface CoverRenderContext {
    /** Qual bloco de estilos usar: web = preview/editor, pdf = Gotenberg. */
    platform: 'webConfigs' | 'pdfConfigs';
    /** Resolve tags de i18n (cover_template_*). No servidor, recebe o mapa do idioma do documento. */
    translate: (tag: string) => string;
    /** Cor de texto quando o item não define. Web: var(--contentPrimary). PDF: cor sólida. */
    defaultTextColor?: string;
}

// * [API pública]

/**
 * Gera um bloco de HTML por item visível do template, na ordem da lista.
 * Itens sem conteúdo (texto/dicionário vazios) não geram bloco — mesmo
 * comportamento do preview atual.
 */
export function renderCoverItemsHtml(
    template: CoverRendererTemplate,
    ctx: CoverRenderContext
): string[] {
    const blocks: string[] = [];

    for (const item of template.items ?? []) {
        if (item.isVisible === false) continue;

        let html: string | null = null;
        switch (item.type) {
            case 'IMAGE':
                html = renderImageItem(item, ctx);
                break;
            case 'TEXT':
                html = renderTextItem(item, ctx);
                break;
            case 'DICTIONARY':
                html = renderDictionaryItem(item, ctx);
                break;
            case 'DIVIDER':
                html = renderDividerItem(item, ctx);
                break;
        }

        if (html != null) blocks.push(html);
    }

    return blocks;
}

/**
 * Recolore o SVG de fundo trocando o fill dos elementos pelos ids em colors.
 * Mesmo padrão regex do Flutter/PdfEngine: id="..." ([^>]*?) fill="..." —
 * permite atributos entre id e fill.
 */
export function applySvgColorModifications(
    svgString: string,
    colors: CoverRendererBackgroundColor[] | undefined
): string {
    let modifiedSvg = svgString;

    for (const colorItem of colors ?? []) {
        if (!colorItem.id?.trim() || !colorItem.color?.trim()) continue;

        const idPattern = escapeRegExp(colorItem.id.trim());
        const color = normalizeHexColor(colorItem.color);

        modifiedSvg = modifiedSvg.replace(
            new RegExp(`id="${idPattern}"([^>]*?)fill="[^"]*"`, 'gi'),
            `id="${colorItem.id}"$1fill="${color}"`
        );

        modifiedSvg = modifiedSvg.replace(
            new RegExp(`id="${idPattern}"([^>]*?)fill='[^']*'`, 'gi'),
            `id="${colorItem.id}"$1fill="${color}"`
        );
    }

    return modifiedSvg;
}

/**
 * Converte boxFit da API (COVER, FILL, fitWidth…) para CSS background-size.
 * Padrão: cover — equivalente ao BoxFit.cover do Flutter.
 */
export function coverBackgroundSizeCss(boxFit?: string): string {
    const key = normalizeEnumKey(boxFit) ?? 'COVER';

    const map: Record<string, string> = {
        FILL: '100% 100%',
        CONTAIN: 'contain',
        COVER: 'cover',
        FIT_WIDTH: '100% auto',
        FIT_HEIGHT: 'auto 100%',
        NONE: 'auto',
        SCALE_DOWN: 'contain',
    };

    return map[key] ?? 'cover';
}

/**
 * Converte imageAlignment da API para CSS background-position.
 * Padrão: center — equivalente ao Alignment.center do Flutter.
 */
export function coverBackgroundPositionCss(imageAlignment?: string): string {
    const key = normalizeEnumKey(imageAlignment) ?? 'CENTER';

    const synonyms: Record<string, string> = {
        RIGHT: 'center right',
        LEFT: 'center left',
        TOP: 'top center',
        BOTTOM: 'bottom center',
        CENTER_TOP: 'top center',
        CENTER_BOTTOM: 'bottom center',
    };

    if (synonyms[key]) return synonyms[key];

    const map: Record<string, string> = {
        CENTER: 'center center',
        TOP_LEFT: 'top left',
        TOP_CENTER: 'top center',
        TOP_RIGHT: 'top right',
        CENTER_LEFT: 'center left',
        CENTER_RIGHT: 'center right',
        BOTTOM_LEFT: 'bottom left',
        BOTTOM_CENTER: 'bottom center',
        BOTTOM_RIGHT: 'bottom right',
    };

    return map[key] ?? 'center center';
}

// * [Itens]

type StyleEntry = [property: string, value: string, important?: boolean];

function renderBaseBlock(
    item: CoverRendererItem,
    ctx: CoverRenderContext,
    innerHtml: string
): string {
    const configs = platformConfigs(item, ctx);
    const styles: StyleEntry[] = [['display', 'block']];

    if (configs?.marginTop != null) styles.push(['margin-top', `${configs.marginTop}px`, true]);
    if (configs?.marginBottom != null)
        styles.push(['margin-bottom', `${configs.marginBottom}px`, true]);
    if (configs?.marginLeft != null) styles.push(['margin-left', `${configs.marginLeft}px`, true]);
    if (configs?.marginRight != null)
        styles.push(['margin-right', `${configs.marginRight}px`, true]);

    const position = item.configStyles?.position;
    if (position) {
        const textAlignMap: Record<string, string> = {
            LEFT: 'left',
            CENTER: 'center',
            RIGHT: 'right',
        };
        styles.push(['text-align', textAlignMap[position] ?? 'left']);
    }

    return `<div style="${styleAttr(styles)}">${innerHtml}</div>`;
}

function renderImageItem(item: CoverRendererItem, ctx: CoverRenderContext): string {
    const configs = platformConfigs(item, ctx);
    const url = item.value?.linkValueConverted ?? item.value?.url;

    if (url) {
        const styles: StyleEntry[] = [
            ['display', 'inline-block'],
            ['vertical-align', 'middle'],
            ['box-sizing', 'border-box'],
        ];
        pushImageDimensions(styles, configs);

        if (configs?.borderColor != null) styles.push(['border-color', configs.borderColor, true]);
        if (configs?.borderSize != null)
            styles.push(['border-width', `${configs.borderSize}px`, true]);
        if (configs?.borderSize != null && configs?.borderColor != null)
            styles.push(['border-style', 'solid', true]);

        if (item.value?.format === 'square') styles.push(['object-fit', 'cover']);

        styles.push(['border-radius', '8px', true]);

        const image = `<img src="${escapeAttr(url)}" style="${styleAttr(styles)}">`;
        return renderBaseBlock(item, ctx, image);
    }

    const styles: StyleEntry[] = [
        ['display', 'inline-block'],
        ['box-sizing', 'border-box'],
        ['background-color', 'var(--backgroundPrimary, #f5f5f5)'],
        ['border', '1px solid var(--borderPrimary, #e0e0e0)'],
        ['border-radius', '8px'],
    ];
    pushImageDimensions(styles, configs);

    return renderBaseBlock(item, ctx, `<div style="${styleAttr(styles)}"></div>`);
}

/**
 * Dimensões da imagem/placeholder com segurança para mobile:
 * nunca permitir overflow horizontal quando o template vier com
 * dimensões desktop fixas.
 */
function pushImageDimensions(styles: StyleEntry[], configs?: CoverRendererStylesPlatform): void {
    const w = configs?.width;
    const h = configs?.height;

    styles.push(['max-width', '100%']);

    if (w != null && h != null) {
        styles.push(
            ['width', `min(${w}px, 100%)`],
            ['height', 'auto'],
            ['aspect-ratio', `${w} / ${h}`]
        );
    } else if (w != null) {
        styles.push(['width', `min(${w}px, 100%)`], ['height', 'auto']);
    } else if (h != null) {
        styles.push(['width', 'auto'], ['height', `${h}px`], ['max-height', `min(100vh, ${h}px)`]);
    } else {
        // Sem dimensões explícitas: evita estourar no mobile (height: 100% +
        // container alto), mantendo proporção visual estável.
        styles.push(
            ['width', '100%'],
            ['height', 'auto'],
            ['aspect-ratio', '4 / 3'],
            ['max-height', 'min(45vh, 260px)']
        );
    }
}

function renderTextItem(item: CoverRendererItem, ctx: CoverRenderContext): string | null {
    let content: string | undefined;

    if (item.toTranslate === true) {
        content = translateTag(item.value?.text, ctx);
    } else {
        content = item.value?.linkValueConverted ?? item.value?.text;
    }

    if (content == null || content === '') return null;

    const text = `<p style="${styleAttr(textStyles(item, ctx, true))}">${content}</p>`;
    return renderBaseBlock(item, ctx, text);
}

function renderDictionaryItem(item: CoverRendererItem, ctx: CoverRenderContext): string | null {
    const value = item.value?.linkValueConverted ?? item.value?.value;
    if (value == null || value === '') return null;

    const key = translateTag(item.value?.tagKeyDescription, ctx);
    if (!key) return null;

    const label = `<p style="${styleAttr(textStyles(item, ctx, true))}">${key}:</p>`;
    const valueElement = `<p style="${styleAttr(textStyles(item, ctx, false))}">${value}</p>`;
    // Flex inline (além da classe) para o fragmento não depender de CSS externo
    // quando embutido no HTML do PDF.
    const container = `<div class="preview-cover-dictionary-container" style="display: flex; gap: 6px;">${label}${valueElement}</div>`;

    return renderBaseBlock(item, ctx, container);
}

function renderDividerItem(item: CoverRendererItem, ctx: CoverRenderContext): string {
    const styles: StyleEntry[] = [
        ['border', 'none'],
        ['border-top', `1px solid ${itemColor(item.configStyles?.color, ctx)}`],
        ['margin', '0'],
    ];

    return renderBaseBlock(item, ctx, `<hr style="${styleAttr(styles)}">`);
}

// * [Estilos de texto]

function textStyles(
    item: CoverRendererItem,
    ctx: CoverRenderContext,
    isBold: boolean
): StyleEntry[] {
    const configs = platformConfigs(item, ctx);
    const styles: StyleEntry[] = [['color', itemColor(item.configStyles?.color, ctx)]];

    if (configs?.fontSize) styles.push(['font-size', normalizeFontSize(configs.fontSize)]);
    if (configs?.fontStyle) styles.push(['font-style', configs.fontStyle]);
    if (configs?.lineHeight) styles.push(['line-height', configs.lineHeight]);

    styles.push(['font-weight', isBold ? '700' : fontWeightFromConfig(configs?.fontWeight)]);

    return styles;
}

function itemColor(colorHex: string | undefined, ctx: CoverRenderContext): string {
    if (!colorHex?.trim()) return ctx.defaultTextColor ?? 'var(--contentPrimary)';

    return normalizeHexColor(colorHex);
}

function normalizeFontSize(fontSize: string): string {
    const normalized = fontSize.replace(/px$/i, '').trim();
    return normalized ? `${normalized}px` : fontSize;
}

function fontWeightFromConfig(fontWeight?: string): string {
    const raw = (fontWeight ?? '400').trim();
    if (!raw) return '400';

    const lower = raw.toLowerCase();
    if (lower === 'bold' || lower === 'bolder') return '700';

    const parsed = Number.parseInt(raw, 10);
    if (!Number.isNaN(parsed) && parsed >= 100 && parsed <= 900) {
        return String(parsed);
    }

    return raw;
}

// * [Helpers]

function platformConfigs(
    item: CoverRendererItem,
    ctx: CoverRenderContext
): CoverRendererStylesPlatform | undefined {
    return item.configStyles?.[ctx.platform];
}

function translateTag(tag: string | undefined, ctx: CoverRenderContext): string {
    const key = tag?.trim() ?? '';
    if (!key) return '';

    return ctx.translate(key);
}

function normalizeHexColor(color: string): string {
    const trimmed = color.trim();
    if (!trimmed || trimmed.startsWith('#')) return trimmed;

    return `#${trimmed}`;
}

function normalizeEnumKey(value?: string): string | null {
    if (!value?.trim()) return null;

    return value
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toUpperCase();
}

function styleAttr(styles: StyleEntry[]): string {
    return styles
        .map(
            ([property, value, important]) =>
                `${property}: ${value}${important ? ' !important' : ''};`
        )
        .join(' ');
}

function escapeAttr(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
